import { Subject } from "rxjs";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { FlashingWaitClient } from "./FlashingWaitClient";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

export class FlashingWaitTimeManager {
  private myNextWaitTime: CubiqTS.Moment;
  private myPendingWaits: Map<string, FlashingWaitClient> = new Map();
  private myWaitTimer: any;

  /** Default time until begins flashing is 60000 miliseconds (60 seconds) */
  public millisecondsBeforeFlashing: number = 60000;
  public flashUpdateSubject: Subject<string>;

  constructor() {
    this.flashUpdateSubject = new Subject<string>();
  }

  public checkOrderForFlashHandle(order: CubiqTS.OrderDataImpl): void {
    try {
      if (order.isAboutToActivate || order.isAboutToExpire || order.isAboutToSuspend) {
        let timerDate: CubiqTS.Moment;
        let now: CubiqTS.Moment = CubiqTS.moment(new Date()).subtract(
          this.millisecondsBeforeFlashing,
        );
        if (order.isAboutToActivate) {
          timerDate = order.activationDts;
          if (
            order.suspensionDetails &&
            (!timerDate ||
              (order.suspensionDetails as CubiqTS.SuspensionDetailsImpl).suspensionToDts.isBefore(
                timerDate,
              ))
          ) {
            timerDate = (order.suspensionDetails as CubiqTS.SuspensionDetailsImpl).suspensionToDts;
          }
        }
        if (order.isAboutToExpire && order.expiryDts) {
          if (!timerDate || order.expiryDts.isBefore(timerDate)) {
            timerDate = order.expiryDts;
          }
        }
        if (order.isAboutToSuspend && order.suspensionDetails) {
          if (
            !timerDate ||
            (order.suspensionDetails as CubiqTS.SuspensionDetailsImpl).suspensionFromDts.isBefore(
              timerDate,
            )
          ) {
            timerDate = (order.suspensionDetails as CubiqTS.SuspensionDetailsImpl)
              .suspensionFromDts;
          }
        }
        if (timerDate && timerDate.isAfter(now)) {
          // clone before subtraction to avoid mutating orderData
          timerDate = timerDate.clone().subtract(this.millisecondsBeforeFlashing);
          this.addWaitUntilHandler(order.orderId.toString(), timerDate);
        }
      } else {
        this.removeWaitUntilHandler(order.orderId.toString());
      }
    } catch (e) {
      logger.out.error(
        "Failed to check order for flashing " + order.orderId + " " + order.orderType + ": " + e,
      );
    }
  }
  /**
   * Add details of a wait-until flashing begins.
   * The wait time is caluclated based on the waitUntilDateTime date minus the default milliseconds delay (e.g. 60 seconds)
   *
   * @param orderID
   * @param waitUntilDateTime The wait time will be based on this value and the millisecondsBeforeFlashing
   *
   */
  private addWaitUntilHandler(orderID: string, waitUntilDateTime: CubiqTS.Moment): void {
    if (!waitUntilDateTime) return;

    logger.out.info("addWaitUntilHandler:" + waitUntilDateTime.toString());
    let now: CubiqTS.Moment = CubiqTS.moment(new Date());

    // This equates to delays greater than 24 days. The timer.delay cannot be
    // greater than int.MAX_VALUE, or it triggers immediately
    if (waitUntilDateTime.diff(now) > Number.MAX_VALUE) return;

    if (waitUntilDateTime.isBefore(now)) {
      // not point in notifying as the adapt process will already calculate the flashing
      return;
    }
    let waitClient: FlashingWaitClient = new FlashingWaitClient(orderID, waitUntilDateTime);

    this.myPendingWaits.set(orderID, waitClient);

    if (
      !this.myWaitTimer ||
      !this.myNextWaitTime ||
      this.myNextWaitTime.isAfter(waitUntilDateTime)
    ) {
      this.setWaitTimer(waitUntilDateTime);
    }
  }

  public removeWaitHandler(order: CubiqTS.OrderDataImpl): void {
    try {
      let orderId: string = order.orderId as any;

      if (order.orderId != null && typeof order.orderId !== "string") {
        orderId = order.orderId.toString();
      }

      this.removeWaitUntilHandler(orderId);
    } catch (e) {
      logger.out.error(
        "Failed to remove wait until handler " + order.orderId + " " + order.orderType + ": " + e,
      );
    }
  }

  /**
   * Remove a handler. If the waitType is defined, it removes just that one handler. If no
   * waitType is specified, it removes all handlers for that orderID.
   * @param callbackHandler
   *
   */
  public removeWaitUntilHandler(orderID: string): void {
    if (this.myPendingWaits.get(orderID)) {
      this.myPendingWaits.delete(orderID);

      if (!this.myPendingWaits.size) {
        // Nothing else waiting so stop
        this.myNextWaitTime = null;
        if (this.myWaitTimer) {
          clearInterval(this.myWaitTimer);
        }
      } else {
        this.setupNextWaitTime();
      }
    }
  }

  /**
   * Set the wait timer to wake up at specideid nextWaitTime
   * @param now
   * @param nextWaitTime
   *
   */
  private setWaitTimer(nextWaitTime: CubiqTS.Moment): void {
    let now: CubiqTS.Moment = CubiqTS.moment(new Date());
    this.myNextWaitTime = nextWaitTime;

    if (this.myWaitTimer) {
      clearInterval(this.myWaitTimer);
    }

    let delay: number = nextWaitTime.diff(now);
    if (delay < 0) {
      delay = 1;
    }
    this.myWaitTimer = setInterval(this.waitTimerHandler.bind(this), delay);
  }

  /**
   * Find the nearest time from the list of pending waits and restart the timer.
   * Should only be called if there is at least one pending wait
   *
   */
  private setupNextWaitTime(): void {
    let nexTime = null;
    this.myPendingWaits.forEach((value: FlashingWaitClient, key: string) => {
      if (value.waitTime) {
        if (!nexTime || value.waitTime.isBefore(nexTime)) {
          nexTime = value.waitTime;
        }
      }
    });
    if (nexTime) {
      this.setWaitTimer(nexTime);
    }
  }

  /**
   * Handle a timer event for a pending wait event. This will notify any handler registered for
   * specified time, then finally restart the timer if still clients outstanding
   * @param event
   *
   */
  private waitTimerHandler(): void {
    if (this.myPendingWaits.size) {
      this.myPendingWaits.forEach((value: FlashingWaitClient, key: string) => {
        if (value.waitTime.isSameOrBefore(this.myNextWaitTime)) {
          this.myPendingWaits.delete(value.orderID);
          this.flashUpdateSubject.next(value.orderID);
        }
      });
      this.setupNextWaitTime();
    }
  }
}
